import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Row } from "react-bootstrap";
import { FadeLoader } from "react-spinners";
import { removeFromMongoDetail } from "store/HistoricalData/actions";
import moment from "moment";

export default function RemoveFromMongoPop({ show, handleClose, title, id }) {
  const { loading } = useSelector(({ HistoricalData }) => HistoricalData);
  const [data, setData] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    dispatch(
      removeFromMongoDetail({
        data: { _id: id },
        callBack: async (err, response) => {
          const record = response?.data;
          if (record?._id) {
            setData(record);
          }
        },
      }),
    );
  };

  // Define all history sections in order
  const historyRows = [
    {
      name: "BTNC History",
      lastIdKey: "btncHistoryLastId",
      statusKey: "btncHistoryStatus",
      totalCountKey: "btncHistoryTotalCount",
      moveCountKey: "btncHistoryCount",
    },
    {
      name: "Claim History",
      lastIdKey: "claimHistoryLastId",
      statusKey: "claimHistoryStatus",
      totalCountKey: "claimHistoryTotalCount",
      moveCountKey: "claimHistoryCount",
    },
    {
      name: "Coin History",
      lastIdKey: "coinHistoryLastId",
      statusKey: "coinHistoryStatus",
      totalCountKey: "coinHistoryTotalCount",
      moveCountKey: "coinHistoryCount",
    },
    {
      name: "Energy History",
      lastIdKey: "energyHistoryLastId",
      statusKey: "energyHistoryStatus",
      totalCountKey: "energyHistoryTotalCount",
      moveCountKey: "energyHistoryCount",
    },
    {
      name: "Energy Purchase History",
      lastIdKey: "energyPurchaseHistoryLastId",
      statusKey: "energyPurchaseHistoryStatus",
      totalCountKey: "energyPurchaseHistoryTotalCount",
      moveCountKey: "energyPurchaseHistoryCount",
    },
    {
      name: "Game History",
      lastIdKey: "gameHistoryLastId",
      statusKey: "gameHistoryStatus",
      totalCountKey: "gameHistoryTotalCount",
      moveCountKey: "gameHistoryCount",
    },
    {
      name: "Game Jackpot",
      lastIdKey: "gameJackpotLastId",
      statusKey: "gameJackpotStatus",
      totalCountKey: "gameJackpotTotalCount",
      moveCountKey: "gameJackpotCount",
    },
    {
      name: "Game Leaderboard",
      lastIdKey: "gameLeaderboardLastId",
      statusKey: "gameLeaderboardStatus",
      totalCountKey: "gameLeaderboardTotalCount",
      moveCountKey: "gameLeaderboardCount",
    },
    {
      name: "Jackpot",
      lastIdKey: "jackpotLastId",
      statusKey: "jackpotStatus",
      totalCountKey: "jackpotTotalCount",
      moveCountKey: "jackpotCount",
    },
    {
      name: "Leaderboard Transaction",
      lastIdKey: "leaderboardTransactionLastId",
      statusKey: "leaderboardTransactionStatus",
      totalCountKey: "leaderboardTransactionTotalCount",
      moveCountKey: "leaderboardTransactionCount",
    },
    {
      name: "Logs",
      lastIdKey: "logsLastId",
      statusKey: "logsStatus",
      totalCountKey: "logsTotalCount",
      moveCountKey: "logsCount",
    },
  ];

  const getStatusBadge = (status) => {
    const map = {
      completed: "badge badge-success",
      success: "badge badge-success",
      partial: "badge badge-warning",
      deleted: "badge badge-danger",
      pending: "badge badge-secondary",
    };
    return (
      <span className={map[status] || "badge badge-secondary"}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : "-"}
      </span>
    );
  };

  const isMismatch = (totalCount, moveCount) => {
    if (totalCount === null || totalCount === undefined) return false;
    return Number(totalCount) !== Number(moveCount);
  };

  return loading ? (
    <div className="main-loader">
      <FadeLoader size={1000} />
    </div>
  ) : (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="xl"
    >
      <Modal.Header className="py-3 text-dark" closeButton>
        <Modal.Title className="fw-bold text-dark">
          Remove Data Details
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <section className="section">
          {/* Summary Info */}
          {data && (
            <div className="mb-3 d-flex gap-4 flex-wrap">
              <div>
                <strong>Date:</strong>{" "}
                {data.date ? moment(data.date).format("DD MMM YYYY") : "-"}
              </div>
              <div>
                <strong>Overall Status:</strong> {getStatusBadge(data.status)}
              </div>
              <div>
                <strong>Created At:</strong>{" "}
                {data.createdAt
                  ? moment(data.createdAt).format("DD MMM YYYY, hh:mm A")
                  : "-"}
              </div>
              <div>
                <strong>Updated At:</strong>{" "}
                {data.updatedAt
                  ? moment(data.updatedAt).format("DD MMM YYYY, hh:mm A")
                  : "-"}
              </div>
            </div>
          )}

          <div className="section-body">
            <div className="table-responsive">
              <table className="table table table-centered table-nowrap">
                <thead className="thead-light">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Total Count</th>
                    <th>Move Data Count</th>
                  </tr>
                </thead>
                <tbody>
                  {data ? (
                    historyRows.map((row, index) => {
                      const lastId = data[row.lastIdKey];
                      const status = data[row.statusKey];
                      const totalCount = row.totalCountKey
                        ? data[row.totalCountKey]
                        : null;
                      const moveCount = data[row.moveCountKey];
                      const mismatch = isMismatch(totalCount, moveCount);

                      return (
                        <tr
                          key={row.name}
                          style={
                            mismatch
                              ? {
                                  backgroundColor: "#ffe5e5",
                                  color: "#c0392b",
                                }
                              : {}
                          }
                        >
                          <td className="bg-transparent">{index + 1}</td>
                          <td className="bg-transparent">
                            <strong>{row.name}</strong>
                          </td>
                          <td className="bg-transparent">
                            {getStatusBadge(status)}
                          </td>
                          <td className="bg-transparent">
                            {totalCount !== null && totalCount !== undefined ? (
                              totalCount
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </td>
                          <td className="bg-transparent">
                            {moveCount !== undefined ? moveCount : "-"}
                            {mismatch && (
                              <span
                                className="ml-2"
                                title="Mismatch between total count and move data count"
                              >
                                ⚠️
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </Modal.Body>
    </Modal>
  );
}
